import { Loader } from '@fountain/fountain-ui-components';
import {
  ChevronLeftOutlineIcon,
  ChevronRightOutlineIcon,
  Icon,
} from '@fountain/ripple/icon';
import {
  Button,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@material-ui/core';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { TestableTypography } from 'components/_shared/TestableTypography';
import globalMessages from 'shared/global/messages';

import { messages } from './messages';
import { useStyles } from './styles';

// must be ordered lowest to highest
export const ROWS_PER_PAGE = [10, 20, 50, 100];

export interface StyledPaginationProps {
  currentPage?: number;
  lastPage?: number;
  isLoading?: boolean;
  /**
   * Holds every control without taking the pager off screen, for a caller with a
   * write in flight that a page turn would land in the wrong place. Distinct from
   * `isLoading`, which replaces the whole pager with a spinner — that drops the
   * button under the reader's cursor, which is the thing a busy state must not
   * do.
   */
  disabled?: boolean;
  recordsPerPage?: number;
  containerClass?: string;
  fillContainer?: boolean;
  setCurrentPage: (page: number) => void;
  setPerPage?: (perPage: number) => void;
}

export const StyledPagination: FC<StyledPaginationProps> = ({
  currentPage = 1,
  lastPage = currentPage,
  isLoading = false,
  disabled = false,
  recordsPerPage = 20,
  containerClass,
  fillContainer = false,
  setCurrentPage,
  setPerPage,
}) => {
  const intl = useIntl();
  const classes = useStyles();

  // The box takes free text, and callers send the page straight to an API, so
  // a blank or non-numeric entry keeps the current page and a number lands in
  // 1..lastPage. The box is rewritten too: the key below remounts it only when
  // the page changes, so a clamp back onto the current page would leave the
  // typed text on screen.
  const commitTypedPage = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const input = event.target;
    const typed = Number(input.value);
    const page =
      input.value.trim() === '' || !Number.isFinite(typed)
        ? currentPage
        : Math.min(Math.max(Math.round(typed), 1), Math.max(lastPage, 1));
    input.value = String(page);
    if (page !== currentPage) setCurrentPage(page);
  };

  if (isLoading) {
    return (
      <Grid className={classes.root} container justify="center">
        <Loader />
      </Grid>
    );
  }

  // TODO: break up into smaller components where possible
  return (
    <Grid className={containerClass ?? classes.root}>
      <Grid
        alignItems="center"
        className={classes.paginationContent}
        container
        direction="row"
        justify="center"
      >
        {setPerPage && (
          <Grid className={classes.paginationLeftContent} item>
            <Grid alignItems="center" container direction="row">
              <Grid item>
                <Typography variant="body2">
                  <FormattedMessage {...globalMessages.showPerPage} />
                </Typography>
              </Grid>
              <Grid item>
                <Select
                  classes={{ root: classes.rowsPerPageSelect }}
                  disabled={disabled}
                  disableUnderline
                  inputProps={{
                    id: 'rows-per-page',
                    name: 'rowsPerPage',
                  }}
                  data-testid="rowsPerPage"
                  onChange={event => setPerPage(Number(event.target.value))}
                  type="number"
                  value={recordsPerPage}
                >
                  {ROWS_PER_PAGE.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
            </Grid>
          </Grid>
        )}
        <Grid
          className={fillContainer ? '' : classes.pageSelectContainer}
          item
          xs={setPerPage ? 6 : 12}
        >
          <Grid
            alignItems="center"
            container
            direction="row"
            justify="space-between"
          >
            <Grid item>
              <Button
                aria-label={intl.formatMessage(messages.prev)}
                component="div"
                disabled={disabled || currentPage <= 1}
                // Guarded as well as marked. These are `component="div"`, and
                // MUI can only give a non-native button `aria-disabled` and
                // `tabIndex: -1` — a mouse click still reaches the handler, so
                // the disabled state would be advisory without this.
                onClick={() => !disabled && setCurrentPage(currentPage - 1)}
              >
                <Icon icon={ChevronLeftOutlineIcon} size="lg" color="inherit" />
                <Typography>
                  <FormattedMessage {...messages.prev} />
                </Typography>
              </Button>
            </Grid>
            <Grid item>
              <Grid alignItems="center" container>
                <Grid item>
                  <InputLabel htmlFor="paginationCurrentPage">
                    <Typography>
                      <FormattedMessage {...messages.page} />
                    </Typography>
                  </InputLabel>
                </Grid>
                <Grid item>
                  {/*
                    Uncontrolled input exists because input value must be different from 'currentPage'. If input value
                    changes with 'currentPage' then re-render occurs, which won't allow for page inputs > 9.

                    Keyed on `currentPage` so a page turn from anywhere else —
                    Prev, Next, or a caller setting the page — remounts it and
                    the box shows the page it is on. `defaultValue` alone is read
                    once, so the field kept the page the reader arrived on while
                    the list moved underneath it. The key changes only when the
                    page does, which is exactly when the typed value is stale.
                  */}
                  <Input
                    key={currentPage}
                    classes={{
                      input: classes.pageSelectInput,
                      root: classes.pageSelectInputRoot,
                    }}
                    data-testid="paginationCurrentPage"
                    id="paginationCurrentPage"
                    defaultValue={currentPage}
                    disabled={disabled}
                    disableUnderline
                    onBlur={event => !disabled && commitTypedPage(event)}
                    onKeyDown={event =>
                      event.key === 'Enter' &&
                      (event.target as HTMLElement).blur()
                    }
                  />
                </Grid>
                <Grid item>
                  <Typography>
                    <FormattedMessage {...messages.of} />
                  </Typography>
                </Grid>
                <Grid className={classes.lastPage} item>
                  <TestableTypography className={classes.lastPage}>
                    {lastPage}
                  </TestableTypography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Button
                aria-label={intl.formatMessage(globalMessages.next)}
                component="div"
                disabled={disabled || currentPage === lastPage}
                onClick={() => !disabled && setCurrentPage(currentPage + 1)}
              >
                <Typography>
                  <FormattedMessage {...globalMessages.next} />
                </Typography>
                <Icon
                  icon={ChevronRightOutlineIcon}
                  size="lg"
                  color="inherit"
                />
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
